/**
 * Landing page — slimmed down.
 *
 * Sections (5 total, was 7):
 *   1. Hero       — headline + 2 CTAs + 3 trust numbers
 *   2. Pricing    — DB-driven plan cards (moved up so prices are 1 scroll away)
 *   3. Download   — single card with Mac + Windows buttons
 *   4. How it works — 3 minimal steps
 *   5. Footer     — small links + copyright
 */

import { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useI18n } from '../i18n/I18nContext';
import LanguageSwitcher from '../components/LanguageSwitcher';
import Logo from '../components/Logo';
import { plansAPI } from '../services/api';
import { DOWNLOADS } from '../config/downloads';
import {
  CheckCircle, Download, FileText, Clock, Zap,
  LogIn, UserPlus, X, User, LogOut, LayoutDashboard, ChevronDown,
  ArrowRight, Globe,
} from 'lucide-react';
import '../styles/Landing.css';

function LandingPage() {
  const navigate = useNavigate();
  const { user, logout, isAuthenticated } = useAuth();
  const { t, lang, setLang } = useI18n();

  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [userDropdownOpen, setUserDropdownOpen] = useState(false);
  const [dbPlans, setDbPlans] = useState([]);

  const isAuthed = typeof isAuthenticated === 'function' ? isAuthenticated() : !!user;

  // Lock body scroll when the mobile drawer is open.
  useEffect(() => {
    document.body.classList.toggle('mobile-menu-open', mobileMenuOpen);
    return () => document.body.classList.remove('mobile-menu-open');
  }, [mobileMenuOpen]);

  // Close the user dropdown when clicking outside.
  useEffect(() => {
    const close = (e) => {
      if (userDropdownOpen && !e.target.closest('.lp-user-menu')) setUserDropdownOpen(false);
    };
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, [userDropdownOpen]);

  // Fetch plans from the API (admin can edit price/minutes/features/popular).
  useEffect(() => {
    let active = true;
    plansAPI.list()
      .then((res) => { if (active) setDbPlans(Array.isArray(res?.data) ? res.data : []); })
      .catch(() => { /* page just shows zero plans if this fails */ });
    return () => { active = false; };
  }, []);

  const parseFeatures = (raw) => {
    if (!raw) return [];
    if (Array.isArray(raw)) return raw;
    if (typeof raw === 'string') {
      try { const parsed = JSON.parse(raw); return Array.isArray(parsed) ? parsed : []; }
      catch { return []; }
    }
    return [];
  };

  const plans = dbPlans
    .filter((p) => p.is_active && p.is_purchasable)
    .map((p) => ({
      id: p.id,
      name: p.name,
      slug: p.slug,
      minutes: String(p.minutes),
      price: Number(p.price).toFixed(0),
      popular: !!p.is_popular,
      features: parseFeatures(p.features),
    }));

  const goTo = (path) => { setMobileMenuOpen(false); navigate(path); };
  const scrollTo = (id) => {
    setMobileMenuOpen(false);
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const handleLogout = () => {
    logout();
    setUserDropdownOpen(false);
    navigate('/');
  };

  return (
    <div className="lp-shell">
      {/* ─── Header ───────────────────────────────────────────────────── */}
      <header className="lp-header">
        <div className="lp-header-inner">
          <div className="lp-logo">
            <Logo height={32} />
          </div>

          <div className="lp-header-right">
            <div className="lp-lang"><LanguageSwitcher /></div>

            {isAuthed ? (
              <div className="lp-user-menu">
                <button className="lp-user-btn" onClick={() => setUserDropdownOpen(!userDropdownOpen)}>
                  <User size={16} />
                  <span className="lp-user-name">{user?.name}</span>
                  <ChevronDown size={14} className={userDropdownOpen ? 'open' : ''} />
                </button>
                {userDropdownOpen && (
                  <div className="lp-user-dropdown" role="menu">
                    <button onClick={() => { setUserDropdownOpen(false); navigate('/dashboard'); }}>
                      <LayoutDashboard size={16} /> <span>{t('nav.dashboard')}</span>
                    </button>
                    <button onClick={handleLogout}>
                      <LogOut size={16} /> <span>{t('nav.logout')}</span>
                    </button>
                  </div>
                )}
              </div>
            ) : (
              <div className="lp-header-cta">
                <button className="lp-btn-ghost" onClick={() => navigate('/login')}>
                  {t('nav.login')}
                </button>
                <button className="lp-btn-primary" onClick={() => navigate('/register')}>
                  {t('nav.signUp')}
                </button>
              </div>
            )}

            {/* Animated hamburger — CSS bars, no icon swap */}
            <button
              className={`lp-burger${mobileMenuOpen ? ' is-open' : ''}`}
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              aria-label="Toggle menu"
              aria-expanded={mobileMenuOpen}
            >
              <span className="lp-burger-bar" />
            </button>
          </div>
        </div>
      </header>

      {/* ─── Hero ─────────────────────────────────────────────────────── */}
      <section className="lp-hero">
        <div className="lp-hero-inner">
          <div className="lp-hero-tag">
            <Zap size={11} />
            {lang === 'tr' ? 'Yapay Zeka Destekli Transkripsiyon' : 'AI-Powered Transcription'}
          </div>
          <h1 className="lp-hero-title">
            {t('landing.hero.titleStart')}
            <span className="lp-hero-accent">{t('landing.hero.titleHighlight')}</span>
          </h1>
          <p className="lp-hero-sub">{t('landing.hero.subtitle')}</p>

          <div className="lp-hero-cta">
            <button className="lp-btn-primary lp-btn-lg" onClick={() => scrollTo('pricing')}>
              {t('nav.pricing')} <ArrowRight size={18} />
            </button>
            <button className="lp-btn-ghost lp-btn-lg" onClick={() => scrollTo('download')}>
              <Download size={18} /> {t('nav.download')}
            </button>
          </div>

          <div className="lp-trust">
            <div className="lp-trust-item">
              <strong>99%</strong>
              <span>{t('landing.hero.statAccuracy')}</span>
            </div>
            <div className="lp-trust-divider" />
            <div className="lp-trust-item">
              <strong>50+</strong>
              <span>{t('landing.hero.statLanguages')}</span>
            </div>
            <div className="lp-trust-divider" />
            <div className="lp-trust-item">
              <strong>24/7</strong>
              <span>{t('landing.hero.statProcessing')}</span>
            </div>
          </div>
        </div>
      </section>

      <div className="lp-divider" aria-hidden="true" />

      {/* ─── Pricing ──────────────────────────────────────────────────── */}
      <section id="pricing" className="lp-section">
        <div className="lp-section-head">
          <h2>{t('landing.pricing.title')}</h2>
          <p>{t('landing.pricing.subtitle')}</p>
        </div>

        <div className="lp-plans">
          {plans.length === 0 ? (
            <div className="lp-empty">{t('common.loading')}</div>
          ) : plans.map((p) => (
            <div key={p.id} className={`lp-plan ${p.popular ? 'is-popular' : ''}`}>
              {p.popular && <div className="lp-plan-badge">{t('landing.pricing.mostPopular')}</div>}
              <h3 className="lp-plan-name">{p.name}</h3>
              <div className="lp-plan-price">
                <span className="lp-plan-currency">₺</span>
                <span className="lp-plan-amount">{p.price}</span>
                <span className="lp-plan-suffix">
                  {t('landing.pricing.perMinutes', { minutes: p.minutes })}
                </span>
              </div>
              <ul className="lp-plan-features">
                {p.features.map((f, i) => (
                  <li key={i}>
                    <CheckCircle size={16} /> <span>{f}</span>
                  </li>
                ))}
              </ul>
              <button
                className={p.popular ? 'lp-btn-primary lp-btn-full' : 'lp-btn-ghost lp-btn-full'}
                onClick={() => navigate(isAuthed ? '/dashboard?tab=plans' : '/register')}
              >
                {t('landing.pricing.btnGetStarted')}
              </button>
            </div>
          ))}
        </div>
      </section>

      <div className="lp-divider" aria-hidden="true" />

      {/* ─── Download ─────────────────────────────────────────────────── */}
      <section id="download" className="lp-section lp-section-tinted">
        <div className="lp-section-head">
          <h2>{t('landing.download.title')}</h2>
          <p>{t('landing.download.subtitle')}</p>
        </div>

        <div className="lp-download-card">
          <div className="lp-download-icon"><Download size={36} /></div>
          <div className="lp-download-body">
            <h3>{t('landing.download.cardTitle')}</h3>
            <p>{t('landing.download.cardDesc')}</p>
            <div className="lp-download-buttons">
              <a
                href={DOWNLOADS.mac.url}
                className="lp-btn-primary lp-btn-lg"
                download={DOWNLOADS.mac.filename}
              >
                <AppleIcon /> macOS
              </a>
              <a
                href={DOWNLOADS.win.url}
                className="lp-btn-ghost lp-btn-lg"
                download={DOWNLOADS.win.filename}
              >
                <WindowsIcon /> Windows
              </a>
            </div>
          </div>
        </div>
      </section>

      <div className="lp-divider" aria-hidden="true" />

      {/* ─── How it works ─────────────────────────────────────────────── */}
      <section className="lp-section">
        <div className="lp-section-head">
          <h2>{t('landing.process.title')}</h2>
          <p>{t('landing.process.subtitle')}</p>
        </div>

        <div className="lp-steps">
          {[
            { n: '01', i: <FileText size={22} />, title: t('landing.process.step1Title'), desc: t('landing.process.step1Desc') },
            { n: '02', i: <Clock size={22} />,    title: t('landing.process.step2Title'), desc: t('landing.process.step2Desc') },
            { n: '03', i: <Zap size={22} />,      title: t('landing.process.step3Title'), desc: t('landing.process.step3Desc') },
          ].map((s) => (
            <div className="lp-step" key={s.n}>
              <div className="lp-step-num">{s.n}</div>
              <div className="lp-step-icon">{s.i}</div>
              <h3>{s.title}</h3>
              <p>{s.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* ─── Footer ───────────────────────────────────────────────────── */}
      <footer className="lp-footer">
        <div className="lp-footer-main">

          {/* Brand */}
          <div className="lp-footer-brand">
            <div className="lp-footer-logo">
              <Logo height={34} />
            </div>
            <p className="lp-footer-tagline">{t('landing.footer.tagline')}</p>
            <a href="mailto:vitex.app@gmail.com" className="lp-footer-contact-link">vitex.app@gmail.com</a>
          </div>

          {/* Columns */}
          <div className="lp-footer-cols">
            <div className="lp-footer-col">
              <h4 className="lp-footer-col-title">{lang === 'tr' ? 'Ürün' : 'Product'}</h4>
              <button onClick={() => scrollTo('pricing')}>{t('nav.pricing')}</button>
              <button onClick={() => scrollTo('download')}>{t('nav.download')}</button>
              <button onClick={() => navigate('/login')}>{t('nav.login')}</button>
              <button onClick={() => navigate('/register')}>{t('nav.signUp')}</button>
            </div>
            <div className="lp-footer-col">
              <h4 className="lp-footer-col-title">{lang === 'tr' ? 'Yasal' : 'Legal'}</h4>
              <Link to="/kvkk">KVKK</Link>
              <Link to="/gizlilik-politikasi">{t('legal.privacy')}</Link>
              <Link to="/kullanim-kosullari">{t('legal.terms')}</Link>
              <Link to="/cerez-politikasi">{t('legal.cookies')}</Link>
            </div>
          </div>
        </div>

        {/* Bottom bar */}
        <div className="lp-footer-bottom">
          <span>{t('landing.footer.copyright')}</span>
          <div className="lp-footer-lang-toggle" role="group" aria-label="Language">
            <Globe size={13} aria-hidden="true" />
            <button
              className={lang === 'tr' ? 'active' : ''}
              onClick={() => setLang('tr')}
              aria-pressed={lang === 'tr'}
            >TR</button>
            <span className="lp-footer-lang-sep" aria-hidden="true">·</span>
            <button
              className={lang === 'en' ? 'active' : ''}
              onClick={() => setLang('en')}
              aria-pressed={lang === 'en'}
            >EN</button>
          </div>
        </div>
      </footer>

      {/* ─── Mobile drawer + backdrop (outside header, full-screen) ───── */}
      <div
        className={`lp-drawer-overlay${mobileMenuOpen ? ' is-open' : ''}`}
        onClick={() => setMobileMenuOpen(false)}
        aria-hidden="true"
      />

      <nav
        className={`lp-mobile-drawer${mobileMenuOpen ? ' is-open' : ''}`}
        aria-label="Mobile navigation"
      >
        <div className="lp-drawer-header">
          <div className="lp-drawer-logo">
            <Logo height={32} />
          </div>
          <button
            className="lp-drawer-close"
            onClick={() => setMobileMenuOpen(false)}
            aria-label="Close menu"
          >
            <X size={18} />
          </button>
        </div>

        <div className="lp-drawer-links">
          <button className="lp-drawer-link" onClick={() => scrollTo('pricing')}>
            {t('nav.pricing')}
          </button>
          <button className="lp-drawer-link" onClick={() => scrollTo('download')}>
            {t('nav.download')}
          </button>
        </div>

        <div className="lp-drawer-lang">
          <LanguageSwitcher />
        </div>

        <div className="lp-drawer-cta">
          {isAuthed ? (
            <>
              <button className="lp-btn-ghost" onClick={() => goTo('/dashboard')}>
                <LayoutDashboard size={16} /> {t('nav.dashboard')}
              </button>
              <button className="lp-btn-ghost" onClick={handleLogout}>
                <LogOut size={16} /> {t('nav.logout')}
              </button>
            </>
          ) : (
            <>
              <button className="lp-btn-ghost" onClick={() => goTo('/login')}>
                <LogIn size={16} /> {t('nav.login')}
              </button>
              <button className="lp-btn-primary" onClick={() => goTo('/register')}>
                <UserPlus size={16} /> {t('nav.signUp')}
              </button>
            </>
          )}
        </div>
      </nav>
    </div>
  );
}

const WindowsIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
    <path d="M0,0H11.377V11.372H0ZM12.623,0H24V11.372H12.623ZM0,12.623H11.377V24H0ZM12.623,12.623H24V24H12.623Z" />
  </svg>
);

const AppleIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
    <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.81-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z" />
  </svg>
);

export default LandingPage;
